
import { Component, Prop, toNative, Vue } from 'vue-facing-decorator';
import { Grid, GridNoRecords } from '@progress/kendo-vue-grid';
import IdentityCard from '../Common/IdentityCard.vue';
import KebabMenu from '../Common/KebabMenu.vue';
import ModalPopup from '../Common/ModalPopup.vue';
import { EventAttendeeDto } from '@/entities/Participants/EventAttendeeDto';
import ParticipantGroupDto from '@/entities/Participants/ParticipantGroupDto';
import ParticipantExtraInformation from '@/entities/Participants/ParticipantExtraInformation';
import { Root } from '@/main';

@Component({
  components: { Grid, GridNoRecords, IdentityCard, KebabMenu, ModalPopup },
})
class NewParticipantGrid extends Vue {
  // ── Incoming data ────────────────────────────────────────────────────────
  @Prop() rows!: EventAttendeeDto[];
  /** Full group list from EventAttendeeListDto — used to resolve GroupIds → name chips. */
  @Prop({ default: () => [] }) allGroups!: ParticipantGroupDto[];
  /** Master list of all My Information fields from EventAttendeeListDto.AllExtraInformations — used to build grid column headers. */
  @Prop({ default: () => [] }) allExtraInformations!: ParticipantExtraInformation[];

  // ── Server-side pagination props (controlled by parent) ──────────────────
  @Prop({ default: 0 })   serverSkip!: number;
  @Prop({ default: 100 }) serverPageSize!: number;
  @Prop({ default: 0 })   totalCount!: number;

  // ── Column visibility driven by parent (column chooser lives in parent) ──
  @Prop({ default: () => ({}) }) columnVisibility!: Record<string, boolean>;

  /** When true every row on every page appears checked and the bar shows "Selected all X" */
  @Prop({ default: false }) isAllPagesSelected!: boolean;

  // ── Platform icons (existing project SVG assets) ─────────────────────────
  private readonly iosIcon     = require('@/assets/Images/ios-device-icon.svg');
  private readonly androidIcon = require('@/assets/Images/android-device-icon.svg');
  private readonly pwaIcon     = require('@/assets/Images/EventSettingFeaturesAndIntegrationImages/web-app-icon.svg');
  private readonly crossIcon   = require('@/assets/Images/blue-circular-cross-icon.svg');
  private readonly exportIcon  = require('@/assets/Images/export-icon.svg');
  private readonly emailIcon   = require('@/assets/Images/Invite/blue-email-envelope.svg');
  private readonly deleteIcon  = require('@/assets/Images/Delete-icon.svg');
  private readonly revokeIcon  = require('@/assets/Images/revoke-ticket-icon.svg');
  private readonly groupIcon   = require('@/assets/Images/assign-group-icon.svg');
  private readonly deletePopupIcon = require('@/assets/Images/Delete-popup-icon.svg');
  private readonly sendEmailPopupIcon = require('@/assets/Images/send-email-popup-icon.svg');

  // Max group chips shown before "+N" overflow badge
  private readonly maxVisibleGroups = 2;

  // Controlled sort state — kept in sync with Kendo Grid so direction persists across data reloads
  private sort: Array<{ field: string; dir: string }> = [];

  // Popup state
  private showModalPopup = false;
  private modalPopupIcon = '';
  private modalPopupBodyContent = '';
  private modalPopupHeading = '';
  private modalPopupBodyHeading = '';
  private modalPopupShowCancelButton = false;
  private modalPopupCancelButtonText = '';
  private modalPopupConfirmButtonText = '';
  private showCloseButton = false;

  // Tracks which action is awaiting confirmation and any associated payload
  private pendingModalAction = '';
  private pendingSendEmailItem: any = null;
  private showDeleteDropdown = false;
  private hasVerticalScroll = true;
  private scrollbarObserver: ResizeObserver | null = null;

  private mounted() {
    document.addEventListener('click', this.CloseDeleteDropdown);
    Root.on('delete-participant', () => this.OnDeleteAction('delete-from-event'));
    this.$nextTick(() => this.observeGridScrollbar());
  }

  private beforeUnmount() {
    document.removeEventListener('click', this.CloseDeleteDropdown);
    if (this.scrollbarObserver) {
      this.scrollbarObserver.disconnect();
      this.scrollbarObserver = null;
    }
  }

  private observeGridScrollbar() {
    const content = this.$el?.querySelector('.k-grid-content') as HTMLElement | null;
    const header = this.$el?.querySelector('.k-grid-header') as HTMLElement | null;
    if (!content || !header) return;

    const sync = () => {
      this.hasVerticalScroll = content.scrollHeight > content.clientHeight;
    };

    sync();
    this.scrollbarObserver = new ResizeObserver(sync);
    this.scrollbarObserver.observe(content);
  }

  private CloseDeleteDropdown() {
    this.showDeleteDropdown = false;
  }

  // ── Selection state — tracked by Id to avoid mutating DTOs ──────────────
  private selectedIds: number[] = [];

  // ── Derived rows ──────────────────────────────────────────────────────────
  /**
   * Rows passed to the Kendo grid. Each item is a shallow spread of the source
   * EventAttendeeDto enriched with three computed fields:
   *   • selected   — driven by selectedIds (for the Kendo checkbox column)
   *   • groups     — resolved group name strings from GroupIds + allGroups
   *   • OverallStatus — getter result materialised so the spread includes it
   */
  private get gridRows(): any[] {
    return (this.rows || []).map((item) => ({
      ...item,
      OverallStatus: item.OverallStatus,
      groups: this.allGroups
        .filter((g) => item.GroupIds.includes(g.Id))
        .map((g) => g.Name),
      selected: this.isAllPagesSelected || this.selectedIds.includes(item.Id),
    }));
  }

  private get selectedDataItems(): EventAttendeeDto[] {
    return (this.rows || []).filter((item) => this.selectedIds.includes(item.Id));
  }

  private get areAllSelected(): boolean {
    return this.isAllPagesSelected ||
      ((this.rows || []).length > 0 &&
        (this.rows || []).every((item) => this.selectedIds.includes(item.Id)));
  }

  // ── Selection handlers ────────────────────────────────────────────────────
  private onHeaderSelectionChange(event: any) {
    const checked = event.event?.target?.checked ?? false;
    if (!checked && this.isAllPagesSelected) {
      this.$emit('clear-select-all');
    }
    this.selectedIds = checked ? (this.rows || []).map((item) => item.Id) : [];
    this.$emit('selection-change', this.selectedIds.length);
  }

  private onSelectionChange(event: any) {
    const item = event.dataItem;
    if (!item) {
      return;
    }
    // When all pages were selected, seed selectedIds with the current page before toggling
    if (this.isAllPagesSelected) {
      this.selectedIds = (this.rows || []).map((r) => r.Id);
      this.$emit('clear-select-all');
    }
    const id = item.Id as number;
    const idx = this.selectedIds.indexOf(id);
    if (idx === -1) {
      this.selectedIds.push(id);
    } else {
      this.selectedIds.splice(idx, 1);
    }
    this.$emit('selection-change', this.selectedIds.length);
  }
  private RowClicked(event: any) {
    const item = event.dataItem;
    if (!item) {
      return;
    }
    this.$emit('row-clicked', item);
  }
  private DeselectAll() {
    this.selectedIds = [];
    if (this.isAllPagesSelected) {
      this.$emit('clear-select-all');
    }
    this.$emit('selection-change', 0);
  }

  private SelectAll() {
    this.selectedIds = (this.rows || []).map((item) => item.Id);
    this.$emit('select-all');
  }

  private OnBulkAction(action: string) {
    this.$emit('bulk-action', { action, selectedItems: this.selectedDataItems });
  }

  private OnDeleteAction(action: string) {
    this.showDeleteDropdown = false;
    this.ShowDeletePopup(action);
  }

  private ShowDeletePopup(action: string) {
    this.pendingModalAction = action;
    this.modalPopupHeading = this.$t('Participant.Grid.DeletePopupTitle').toString();
    this.modalPopupBodyHeading = '';
    this.modalPopupBodyContent = action === 'delete-from-event'
      ? this.$t('Participant.Grid.DeleteFromThisEventBodyContent').toString()
      : this.$t('Participant.Grid.DeleteFromAllEventsBodyContent').toString();
    this.modalPopupShowCancelButton = true;
    this.modalPopupCancelButtonText = this.$t('CancelButton').toString();
    this.modalPopupConfirmButtonText = this.$t('Button.Delete').toString();
    this.modalPopupIcon = this.deletePopupIcon;
    this.showCloseButton = true;
    this.showModalPopup = true;
  }

  private ConfirmClick(confirmed: boolean) {
    this.showModalPopup = false;
    if (confirmed) {
      if (this.pendingModalAction === 'send-invite') {
        this.$emit('row-action', { action: 'send-invite', dataItem: this.pendingSendEmailItem });
      } else if (this.pendingModalAction === 'delete-from-event') {
        this.$emit('bulk-action', { action: 'delete-from-event', selectedItems: this.selectedDataItems });
      } else if (this.pendingModalAction === 'delete-from-all-events') {
        this.$emit('bulk-action', { action: 'delete-from-all-events', selectedItems: this.selectedDataItems });
      }
    }
    this.pendingModalAction = '';
    this.pendingSendEmailItem = null;
  }

  // ── Column definitions ────────────────────────────────────────────────────
  private get columns(): any[] {
    const isVisible = (field: string) => this.columnVisibility[field] !== false;

    const emailColumn = [
      { field: 'email', title: 'Email', width: 320, locked: true, lockable: false, cell: 'cellEmail' },
    ];

    const profileChildren = [
      ...(isVisible('name')    ? [{ field: 'name',    title: 'Name',    width: 220, cell: 'cellName' }] : []),
      ...(isVisible('Title')   ? [{ field: 'Title',   title: 'Title',   width: 180 }] : []),
      ...(isVisible('Company') ? [{ field: 'Company', title: 'Company', width: 180 }] : []),
    ];

    const registrationChildren = [
      ...(isVisible('InviteEmailStatus')  ? [{ field: 'InviteEmailStatus',  title: 'Invite email',  width: 220, cell: 'cellInviteEmail' }] : []),
      ...(isVisible('RegistrationStatus') ? [{ field: 'RegistrationStatus', title: 'Status',        width: 140, cell: 'cellStatus' }]     : []),
      ...(isVisible('RegisteredOn')       ? [{ field: 'RegisteredOn',       title: 'Registered on', width: 220, cell: 'cellDateTime' }]   : []),
    ];

    const ticketChildren = [
      ...(isVisible('ParticipantTicketDetails') ? [{ field: 'ParticipantTicketDetails', title: 'Tickets', width: 380, cell: 'cellTickets' }] : []),
    ];

    const checkedInChildren = [
      ...(isVisible('CheckInDateTime') ? [{ field: 'CheckInDateTime', title: 'Checked-in on', width: 220, cell: 'cellDateTime' }] : []),
    ];

    // 'JoinedByIOS' is used as the column key; the custom cell template reads all three booleans
    const appChildren = [
      ...(isVisible('JoinedByIOS') ? [{ field: 'JoinedByIOS', title: 'Platform', width: 160, cell: 'cellPlatform' }] : []),
    ];

    const groupsChildren = [
      ...(isVisible('groups') ? [{ field: 'groups', title: 'Groups', width: 420, cell: 'cellGroups' }] : []),
    ];

    return [
      { field: 'selected', width: 60, columnMenu: false, headerSelectionValue: this.areAllSelected, locked: true },
      { title: '', locked: true, lockable: false, children: emailColumn },
      ...(profileChildren.length ? [{ title: 'PROFILE', locked: false, lockable: false, children: profileChildren }] : []),
      ...(registrationChildren.length ? [{ title: 'REGISTRATION',    children: registrationChildren }] : []),
      ...(ticketChildren.length       ? [{ title: 'TICKET DETAILS',  children: ticketChildren }]        : []),
      ...(checkedInChildren.length    ? [{ title: 'SELF CHECKED-IN', children: checkedInChildren }]    : []),
      ...(appChildren.length          ? [{ title: 'APP DETAILS',     children: appChildren }]          : []),
      ...(groupsChildren.length       ? [{ title: 'GROUPS DETAILS',  children: groupsChildren }]       : []),
      ...this.extraInfoColumns,
      { field: 'actions', title: ' ', width: 40, cell: 'cellActions', locked: true, lockable: false },
    ];
  }

  // Builds dynamic EXTRA INFORMATION columns from AllExtraInformations (the event-level master list)
  private get extraInfoColumns(): any[] {
    if (!this.allExtraInformations?.length) {
      return [];
    }

    const children = this.allExtraInformations
      .filter((info) => this.columnVisibility[info.KeyValue] !== false)
      .map((info) => ({ field: info.KeyValue, title: info.KeyValue, width: 180, cell: 'cellExtraInfo' }));

    return children.length ? [{ title: 'EXTRA INFORMATION', children }] : [];
  }

  // ── Pager configuration ───────────────────────────────────────────────────
  private get pagerOptions() {
    return {
      buttonCount: 5,
      info: true,
      type: 'numeric',
      pageSizes: false,
      // pageSizes: [5, 10, 50, 100, 200],
      previousNext: true,
      responsive: false,
      messages: {
        // {0} = first item on page, {1} = last item on page, {2} = total
        display: 'Showing {0} - {1} of {2} participants',
      },
    };
  }

  // Emit page-change so the parent can re-fetch the new page from the server
  private onPageChange(event: any) {
    const page = event?.page ?? event;
    this.$emit('page-change', { skip: page?.skip ?? 0, take: page?.take ?? this.serverPageSize });
  }

  private onSortChange(e: { sort: Array<{ field: string; dir: string }> }) {
    this.sort = e.sort;
    const s = e.sort[0];
    if (s) this.$emit('sort-change', { field: s.field, dir: s.dir.toUpperCase() });
  }

  private EmitRowAction(action: string, dataItem: any) {
    this.$emit('row-action', { action, dataItem });
  }

  // ── Cell helpers ──────────────────────────────────────────────────────────
  private getName(item: any): string {
    const first = item?.firstName || item?.FirstName || '';
    const last  = item?.lastName  || item?.LastName  || '';
    const full  = `${first as string} ${last as string}`.trim();
    return full || item?.name || item?.Name || '-';
  }

  // Supports dot-notation field paths (e.g. "address.city")
  private resolveField(item: any, field: string): any {
    if (!item || !field) {
      return undefined;
    }
    if (field.indexOf('.') === -1) {
      return item[field];
    }
    return field.split('.').reduce((acc: any, key: string) => (acc ? acc[key] : undefined), item);
  }

  private formatDateTime(val?: string): string {
    if (!val) {
      return '-';
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) {
      return '-';
    }
    return d.toLocaleString();
  }

  private GetParticipantExtraInfo(dataItem: any, field: string): string {
    return (dataItem.ExtraInformations || [])
      .find((e: any) => e.KeyValue === field)?.KeyInformation ?? '-';
  }
  private getEmailStatusClass(overallStatus: number) {
  // Based on EnumEmailStatuses in EmailStatusEnum.cs
    switch (overallStatus) {
    case -2: return {'badge-grey': true}; // UnSent        – grey
    case -1: return {'badge-yellow': true}; // EmailSending  – amber
    case  1: return {'badge-green': true}; // EmailSent     – green
    case  2: return {'badge-green': true}; // EmailDelivered– green
    case  3: return {'badge-green': true}; // EmailOpen     – green
    case  4: return {'badge-green': true}; // EmailClick    – green
    case  5: return {'badge-red': true}; // EmailBounce  – red
    case  6: return {'badge-grey': true}; // EmailRejected – grey  (force "Email delivered" per facade)
    case  7: return {'badge-green': true}; // EmailAccepted – green (force "Email delivered" per facade)
    case  8: return {'badge-green': true}; // DirectAccepted– green (force "Email delivered" per facade)
    default: return {'badge-grey': true}; // fallback      – grey
    }
  }
  private SendEmail(dataItem: any) {
    this.pendingSendEmailItem = dataItem;
    this.pendingModalAction = 'send-invite';
    this.modalPopupHeading = this.$t('Participant.SendInvitationEmail').toString();
    this.modalPopupBodyHeading = '';
    this.modalPopupBodyContent = this.$t('Participant.Grid.SendInviteBodyContent').toString();
    this.modalPopupShowCancelButton = true;
    this.modalPopupCancelButtonText = this.$t('CancelButton').toString();
    this.modalPopupConfirmButtonText = this.$t('Button.Send').toString();
    this.modalPopupIcon = this.sendEmailPopupIcon;
    this.showCloseButton = true;
    this.showModalPopup = true;
  }
}

export default toNative(NewParticipantGrid);
